<template>
  <div class="livestream-container">
    <img
      v-if="showLiveImage"
      src="/assets/Visual_KOM2022_16_9.jpg"
      style="position: absolute; height: 100%; width: 100vw; z-index: 100"
      alt="Vorschau Live"
    />
    <video ref="videoPlayer" class="video-js" controls></video>
    <div v-if="transcriptionStarted" class="transcription">
      {{ transcriptionResult }}
    </div>
  </div>
</template>

<script>
import { db } from "@/firebase";
import videojs from "video.js";
import iso from "@/iso-langs";
import "videojs-contrib-hls";
// import AwsTranscribe from "../helpers/aws-socket-transcribe.js";
import { mapState } from "vuex";

export default {
  name: "LiveStream",
  data() {
    return {
      eventData: undefined,
      origStream: undefined,
      player: undefined,
      streamList: [],
      isoLangList: [],
      currTime: new Date().getTime(),
      transcriptionStarted: false,
      audioContext: null,
      awsTranscriber: null,
      audioSrc: null,
      selectedLang: null,
      supportedTranscribeLangs: {
        eng: "en-US",
        deu: "de-DE",
        zho: "zh-CN",
        fra: "fr-FR",
        ita: "it-IT",
        spa: "es-US",
        jpn: "ja-JP",
        kor: "ko-KR",
        por: "pt-BR",
      },
    };
  },
  computed: {
    ...mapState({
      transcriptionResult: (state) => state.mediasoup.transcriptionResult,
    }),
    showLiveImage() {
      return (
        this.$route.params.eventId == "jDMGkR5hniYvu3snhWEZ" && this.currTime < 1642320000000 // 16.1.2022 9:00
      );
    },
  },
  /**
   * Used for object cleanup
   */
  async beforeDestroy() {
    this.audioContext?.close();
    this.awsTranscriber?.closeConnection();
    await new Promise((r) => setTimeout(r, 2000));
  },
  created: async function () {
    setInterval(() => {
      this.currTime = new Date().getTime();
    }, 10000);
    const doc = db.doc(`organizations/${this.$route.params.organizationId}/events/${this.$route.params.eventId}`);
    const eventData = (await doc.get()).data();

    const tempComb = [];
    for (const comb of eventData.langComb) {
      if (tempComb.find((lang) => comb.lang2 == lang.lang2) == undefined) {
        tempComb.push({ lang2: comb.lang2 });
        this.isoLangList.push(iso[comb.lang2]);
      }
      if (tempComb.find((lang) => comb.lang1 == lang.lang2) == undefined) {
        tempComb.push({ lang2: comb.lang1 });
        this.isoLangList.push(iso[comb.lang1]);
      }
    }
    tempComb.unshift({ lang2: "orig" });

    eventData.langComb = tempComb;
    this.eventData = eventData;

    this.player = videojs(
      this.$refs.videoPlayer,
      {
        html5: {
          vhs: {
            overrideNative: !videojs.browser.IS_SAFARI,
          },
          nativeAudioTracks: false,
          nativeVideoTracks: false,
        },
        liveui: true,
        autoplay: "any",
      },
      () => {
        this.player.fluid(true);
        this.player.aspectRatio("16:9");

        // Get the current player's AudioTrackList object.
        var audioTrackList = this.player.audioTracks();

        // Listen to the "change" event.
        audioTrackList.addEventListener("change", () => {
          console.log("File: Livestream.vue, audio track changed.");
          // Log the currently enabled AudioTrack label.
          for (var i = 0; i < audioTrackList.length; i++) {
            var track = audioTrackList[i];

            if (track.enabled) {
              // Closing existing transcriber on track change
              this.awsTranscriber?.closeConnection();
              this.transcriptionStarted = false;

              this.selectedLang = track.id;
              this.player.src({
                type: "application/x-mpegURL",
                src: this.streamList[track.id],
              });
              this.player.play();
              this.init(track.id);
              return;
            }
          }
        });

        const origProxyTaskName = "qonda-" + this.cleanEventName() + "-orig";

        let src = "";

        if (this.$route.query.language == undefined) {
          src = "https://cloudfront.qonda.cloud/hls/" + origProxyTaskName + "/index.m3u8";
          if (this.eventData.langproxy && this.eventData.langproxy.origUrl) {
            src = this.eventData.langproxy.origUrl;
          }
        } else {
          const iso_639_1 = this.isoLangList.find(
            (l) => l["639-1"] != undefined && l["639-1"].toLowerCase() == this.$route.query.language.toLowerCase(),
          );
          const iso_639_2 = this.isoLangList.find(
            (l) => l["639-2"] != undefined && l["639-2"].toLowerCase() == this.$route.query.language.toLowerCase(),
          );
          const de = this.isoLangList.find(
            (l) => l.de[0] != undefined && l.de[0].toLowerCase() == this.$route.query.language.toLowerCase(),
          );
          const en = this.isoLangList.find(
            (l) => l.en[0] != undefined && l.en[0].toLowerCase() == this.$route.query.language.toLowerCase(),
          );
          const fr = this.isoLangList.find(
            (l) => l.fr[0] != undefined && l.fr[0].toLowerCase() == this.$route.query.language.toLowerCase(),
          );
          const native = this.isoLangList.find(
            (l) => l.native != undefined && l.native.toLowerCase() == this.$route.query.language.toLowerCase(),
          );

          if (
            iso_639_1 != undefined ||
            iso_639_2 != undefined ||
            de != undefined ||
            en != undefined ||
            fr != undefined ||
            native != undefined
          ) {
            const lang =
              (iso_639_1 != undefined && iso_639_1["639-2"]) ||
              (iso_639_2 != undefined && iso_639_2["639-2"]) ||
              (de != undefined && de["639-2"]) ||
              (en != undefined && en["639-2"]) ||
              (fr != undefined && fr["639-2"]) ||
              (native != undefined && native["639-2"]);

            const taskName = "qonda-" + this.cleanEventName() + "-" + lang;

            src = "https://cloudfront.qonda.cloud/hls/" + taskName + "/index.m3u8";
          } else {
            src = "https://cloudfront.qonda.cloud/hls/" + origProxyTaskName + "/index.m3u8";
          }
        }

        this.player.src({ type: "application/x-mpegURL", src });
        this.player.play();

        this.init();

        // const fallBackSubtitle = `If your audio is muted, please turn on volume to see subtitle. Waiting for subtitle....`;
        // this.$store.commit(
        //   "mediasoup/set_transcriptionResult",
        //   fallBackSubtitle
        // );

        // // Tweaking video-js caption button styles
        // let videoCaption = document.querySelector(".vjs-subs-caps-button");
        // videoCaption.style.setProperty("display", "block", "important");

        // // AudioContext for capturing video element's audio
        // const AudioContext = window.AudioContext || window.webkitAudioContext;
        // this.audioContext = new AudioContext();
        // this.audioSrc = this.audioContext.createMediaElementSource(
        //   this.$refs.videoPlayer
        // );
        // // PLaying the audio
        // this.audioSrc.connect(this.audioContext.destination);

        // // Resuming audioContext on mute toggle click
        // this.player.controlBar.volumePanel.muteToggle.on("click", async () => {
        //   if (this.audioContext.state === "suspended") {
        //     await this.audioContext.resume();
        //   }
        // });

        // // Resuming audioContext on volume change
        // this.player.controlBar.volumePanel.volumeControl.volumeBar.on(
        //   "click",
        //   async () => {
        //     if (this.audioContext.state === "suspended") {
        //       await this.audioContext.resume();
        //     }
        //   }
        // );
        // // Starting transcription on caption button click
        // videoCaption.addEventListener("click", async () => {
        //   // Resuming audioContext if not already
        //   if (this.audioContext.state === "suspended") {
        //     await this.audioContext.resume();
        //   }
        //   // Starting transcription
        //   if (!this.transcriptionStarted) {
        //     const supportedLang = Object.keys(
        //       this.supportedTranscribeLangs
        //     ).find(lang => lang === this.selectedLang);
        //     // console.log("supported lang", supportedLang);
        //     // Checking if lang supports transcription
        //     if (supportedLang) {
        //       let dest = this.audioContext.createMediaStreamDestination();
        //       this.audioSrc.connect(dest);

        //       this.awsTranscriber = new AwsTranscribe(
        //         this.supportedTranscribeLangs[this.selectedLang]
        //       );
        //       this.awsTranscriber.startTranscribe(dest.stream);
        //       this.transcriptionStarted = true;
        //     } else {
        //       this.$buefy.toast.open({
        //         message: this.$t("subtitleLangNotSupported"),
        //         type: "is-danger"
        //       });
        //     }
        //   } else {
        //     this.awsTranscriber?.closeConnection();
        //     this.transcriptionStarted = false;
        //   }
        // });
        // // Removing caption menu items from DOM
        // videoCaption.addEventListener("mouseover", function() {
        //   let item = videoCaption.querySelector(".vjs-menu");
        //   if (item) item.parentNode.removeChild(item);
        // });
      },
    );
  },
  methods: {
    addTrack(data, lang) {
      if (lang == data.id || (lang == undefined && data.kind == "main")) {
        data.enabled = true;
      }
      // Create a track object.
      const track = new videojs.AudioTrack(data);

      try {
        // Remove it from the audio track list.
        this.player.audioTracks().addTrack(track);
      } catch (e) {
        console.error("File: Livestream.vue, addTrack error.", e);
      }
    },
    cleanEventName() {
      return encodeURIComponent(this.eventData.name.toLowerCase().replace(/[^a-zA-Z0-9]/g, ""));
    },
    init(activeLang) {
      setTimeout(() => {
        // Get the track we created in an earlier example.
        var track = this.player.audioTracks().getTrackById("default");

        // Remove it from the audio track list.
        this.player.audioTracks().removeTrack(track);

        for (const lang of this.eventData.langComb) {
          const taskName = "qonda-" + this.cleanEventName() + "-" + lang.lang2;
          // console.log(
          //   this.eventData.langproxy && this.eventData.langproxy.origUrl
          // );
          this.streamList[lang.lang2] = "https://cloudfront.qonda.cloud/hls/" + taskName + "/index.m3u8";

          const isoLang = iso[lang.lang2];
          if (lang.lang2 == "orig") {
            lang.iso = "Original";
            lang.iso639 = "en";
            if (this.eventData.langproxy && this.eventData.langproxy.origUrl) {
              this.streamList[lang.lang2] = this.eventData.langproxy.origUrl;
            }
          } else if (isoLang == undefined || isoLang.en[0] == undefined) {
            lang.iso = lang.lang2;
            lang.iso639 = lang.lang2;
          } else {
            // console.log(isoLang);
            lang.iso = isoLang.en[0];
            lang.iso639 = isoLang["639-1"];
          }

          this.addTrack(
            {
              id: lang.lang2,
              kind: lang == "orig" ? "main" : "translation",
              label: lang.iso,
              language: lang.iso639,
            },
            activeLang,
          );
        }
      }, 1000);
    },
  },
};
</script>

<style src="video.js/dist/video-js.css"></style>
<style scoped>
.video-js {
  height: 100vh !important;
  /* width: 98% !important; */
  padding-top: 0px !important;
  margin-left: auto;
  margin-right: auto;
}

video,
.video-js {
  outline: none !important;
}

.transcription {
  z-index: 99999;
  position: absolute;
  bottom: 40px;
  color: white;
  background-color: rgba(0, 0, 0, 0.5);
  border-radius: 2px;
  padding: 0 5px 0 5px;
}

.livestream-container {
  display: flex;
  justify-content: center;
}
</style>
